/**
 * Window-wide drag-and-drop. Opening a file is unavoidable at every startup
 * (nothing survives the session), so it gets the shortest gesture Firefox
 * offers: one drop, no dialog (implementation-plan.md §4.1).
 */

export function enableFileDrop(
  highlight: HTMLElement,
  onFile: (file: File) => void,
  signal?: AbortSignal,
): void {
  // dragenter/dragleave fire per element crossed, so count depth rather than
  // toggling on every child boundary.
  let depth = 0;

  const setActive = (active: boolean) => highlight.classList.toggle('drag-over', active);

  window.addEventListener(
    'dragenter',
    (event) => {
      event.preventDefault();
      depth += 1;
      setActive(true);
    },
    { signal },
  );

  // Without preventDefault here the browser refuses the drop.
  window.addEventListener('dragover', (event) => event.preventDefault(), { signal });

  window.addEventListener(
    'dragleave',
    () => {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setActive(false);
    },
    { signal },
  );

  window.addEventListener(
    'drop',
    (event) => {
      event.preventDefault();
      depth = 0;
      setActive(false);

      const file = event.dataTransfer?.files?.[0];
      if (file) onFile(file);
    },
    { signal },
  );
}
